import { Dispatch, SetStateAction, useMemo } from "react";
import { matchPath, useNavigate } from "react-router-dom";

import { useTranslationContext } from "context/TranslationContext";
import { useGetEntityParamId } from "hooks/useGetEntityParamId";
import cn from "lib/cn";

import { ENav, TNavChild } from "components/Layout/types";

import navStyle from "../NavItem/NavItem.module.css";
import styles from "./NavItemChild.module.css";

export interface NavItemChildProps {
  id?: string;
  nav: TNavChild;
  name: ENav;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
  onClick?: (href: string, name: string) => void;
}

const NavItemChild = ({
  id,
  nav,
  name,
  onClick,
  setCollapsed,
}: NavItemChildProps) => {
  const navigate = useNavigate();
  const paramId = useGetEntityParamId();
  const { t } = useTranslationContext();

  const isActive = useMemo(() => {
    if (!paramId) {
      return matchPath({ path: nav.href, end: true }, location.pathname || "");
    }
    return nav.href.includes(paramId);
  }, [paramId, nav]);

  const handleClickNav = () => {
    if (name === ENav.COMMUNITY || name === ENav.BUSINESS_UNIT) {
      if (onClick) onClick(nav.href, name);
      return;
    }
    navigate(nav.href);
    setCollapsed(true);
  };

  return (
    <li className="ml-1 -mt-[0.125rem] flex pl-6 gap-1">
      <div className="flex items-center">
        <div
          className={cn(
            "flex flex-col h-full",
            styles.wrapperVerticalSeparator
          )}
        >
          <div className={cn(styles.verticalSeparator)} />
          <div className={styles.verticalCombiner} />
          <div className={cn(styles.verticalSeparator)} />
        </div>
      </div>

      <div className="py-1 w-full">
        <div
          className={cn(
            "w-full h-auto flex gap-2",
            "hover:bg-primary-800",
            "-mt-[0.125rem]",
            isActive && "bg-primary-800 text-primary-300",
            navStyle.navItem
          )}
        >
          <div
            className={cn(
              navStyle.separator,
              isActive && navStyle.separatorActive
            )}
          />
          <div className="w-full h-auto">
            <button
              id={id}
              data-testid={id}
              onClick={handleClickNav}
              className="text-left w-full py-3 pr-3"
            >
              <div className="truncate text-ellipsis w-[12rem]">
                {t(nav.label)}
              </div>
            </button>
          </div>
        </div>
      </div>
    </li>
  );
};

export default NavItemChild;
